import { Link } from "react-router-dom";
import {
  ArrowRight, BellRing, ChefHat, ClipboardCheck, Heart, LifeBuoy,
  Megaphone, PackageCheck, Search, ShoppingBag, Store, Truck, Users,
} from "lucide-react";

const FAQS = [
  {
    q: "How do pickup and delivery work?",
    a: "Each kitchen controls the fulfillment options it offers. Arctic Plate shows the available pickup or delivery choice before checkout.",
  },
  {
    q: "Can I order ahead?",
    a: "Yes. Approved Arctic Plate sellers can offer scheduled preorder Drops with set order and fulfillment windows.",
  },
  {
    q: "Can I follow a local kitchen?",
    a: "Yes. Follow kitchens you like so their openings, menu updates, drops, and restocks can reach you through Arctic Plate.",
  },
];

function Step({ number, icon: Icon, title, body }) {
  return (
    <div className="relative rounded-2xl border border-[#E7E0D8] bg-white p-5 shadow-[0_18px_36px_-26px_rgba(7,24,47,.18)]">
      <div className="flex items-center justify-between">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#C25A2E] text-xs font-black text-white">{number}</span>
        <Icon size={20} className="text-[#0E2A47]" aria-hidden="true" />
      </div>
      <h3 className="mt-4 text-sm font-extrabold text-[#0E2A47]">{title}</h3>
      <p className="mt-1 text-xs leading-5 text-[#0E2A47]/75">{body}</p>
    </div>
  );
}

export default function MarketplaceStorySections({ hasDropSellers = false, phase = "all", showBusinessPromotion = true, showHowItWorks = true }) {
  return (
    <>
      {phase !== "late" && <>
      {showHowItWorks && <section className="border-y border-[#E7E5E4] bg-white px-5 py-10 sm:px-8 lg:px-14 lg:py-14" data-testid="home-how-it-works">
        <div className="mx-auto max-w-[1600px]">
          <div className="max-w-2xl">
            <div className="text-[10px] font-extrabold uppercase tracking-[0.28em] text-[#C25A2E]">Simple from craving to checkout</div>
            <h2 className="mt-1 font-serif text-4xl font-semibold leading-none text-[#0E2A47] sm:text-5xl">How Arctic Plate works</h2>
            <p className="mt-3 text-sm leading-6 text-[#0E2A47]/75 lg:text-base">Find local food, choose the fulfillment that fits your day, and order directly from Alaska businesses.</p>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Step number="1" icon={Search} title="Discover local food" body="Browse dishes, cuisines, and independent Alaska kitchens." />
            <Step number="2" icon={ShoppingBag} title="Choose how you want it" body="Pick pickup or available delivery and order ahead when offered." />
            <Step number="3" icon={ChefHat} title="The kitchen prepares your order" body="The kitchen receives the order and manages preparation from one workspace." />
            <Step number="4" icon={PackageCheck} title="Pick up or get delivery" body="Track the order through fulfillment and come back to the kitchens you love." />
          </div>
        </div>
      </section>}

      <section id="weekly-drops-story" className="bg-[#FAFAF8] px-5 py-10 sm:px-8 lg:px-14 lg:py-14" data-testid="home-weekly-drops">
        <div className="mx-auto grid max-w-[1600px] gap-7 lg:grid-cols-[1.1fr_.9fr] lg:items-center">
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-[0.28em] text-[#C25A2E]">Limited menus · preorder windows</div>
            <h2 className="mt-1 font-serif text-4xl font-semibold leading-none text-[#0E2A47] sm:text-5xl">Weekly drops & preorders</h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-[#0E2A47]/75 lg:text-base">
              Approved Arctic Plate sellers can open a limited menu, cap quantities, collect preorders, and prepare only what customers actually ordered.
            </p>
            <div className="mt-5 flex flex-wrap gap-2 text-xs font-bold text-[#0E2A47]">
              <span className="rounded-full border border-[#E7E0D8] bg-white px-3 py-2"><BellRing size={13} className="mr-1 inline" /> Follow for alerts</span>
              <span className="rounded-full border border-[#E7E0D8] bg-white px-3 py-2"><ClipboardCheck size={13} className="mr-1 inline" /> Limited quantities</span>
              <span className="rounded-full border border-[#E7E0D8] bg-white px-3 py-2"><PackageCheck size={13} className="mr-1 inline" /> Planned fulfillment</span>
            </div>
          </div>
          <div className="rounded-[1.75rem] border border-[#E7E0D8] bg-white p-5 shadow-[0_20px_46px_-26px_rgba(84,50,24,.28)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#C25A2E]">Never miss a drop</div>
                <h3 className="mt-1 text-xl font-extrabold text-[#0E2A47]">Follow your favorite kitchens.</h3>
              </div>
              <Heart size={24} className="text-[#C25A2E]" aria-hidden="true" />
            </div>
            <p className="mt-2 text-sm leading-6 text-[#0E2A47]/75">
              Arctic Plate can surface openings, menu updates, new drops, and restocks from businesses you follow.
            </p>
            <Link to={hasDropSellers || !showBusinessPromotion ? "/" : "/sell-food"} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-full bg-[#C25A2E] px-5 text-xs font-extrabold text-white">
              {hasDropSellers || !showBusinessPromotion ? "Explore local food" : "Bring your food business"} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      </>}
      {phase !== "early" && <>
      {showBusinessPromotion && (
        <section className="bg-white px-5 py-10 sm:px-8 lg:px-14 lg:py-14" data-testid="home-seller-value">
                <div className="mx-auto grid max-w-[1600px] overflow-hidden rounded-[2rem] border border-[#E7E0D8] bg-white lg:grid-cols-[1.05fr_.95fr]">
                  <div className="p-6 sm:p-8 lg:p-10">
                    <div className="text-[10px] font-extrabold uppercase tracking-[0.28em] text-[#C25A2E]">For Alaska food businesses</div>
                    <h2 className="mt-2 font-serif text-4xl font-semibold leading-none text-[#0E2A47] sm:text-5xl">Your storefront. Your customers. More ways to be discovered.</h2>
                    <p className="mt-4 max-w-2xl text-sm leading-6 text-[#0E2A47]/75 lg:text-base">
                      Run orders, menu updates, pickup or delivery, customer messages, followers, and eligible preorder drops from one Arctic Plate business workspace.
                    </p>
                    <Link to="/sell-food" className="mt-6 inline-flex min-h-12 items-center gap-2 rounded-full bg-[#0E2A47] px-6 text-sm font-extrabold text-white">
                      Sell on Arctic Plate <ArrowRight size={15} />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-px bg-[#E7E0D8]">
                    {[
                      [Store, "Your own storefront"],
                      [Users, "Marketplace discovery"],
                      [Megaphone, "Follower updates"],
                      [Truck, "Pickup & delivery tools"],
                      [Heart, "Verified order reviews"],
                      [ClipboardCheck, "Orders in one place"],
                    ].map(([Icon, label]) => (
                      <div key={label} className="flex min-h-[116px] flex-col justify-center bg-white p-5 text-[#0E2A47]">
                        <Icon size={20} className="text-[#C25A2E]" aria-hidden="true" />
                        <div className="mt-3 text-sm font-extrabold">{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
      )}

      <section className="bg-[#FAFAF8] px-5 py-9 sm:px-8 lg:px-14" data-testid="home-support">
        <div className="mx-auto flex max-w-[1600px] flex-col gap-5 rounded-[1.75rem] border border-[#E7E0D8] bg-white p-6 sm:flex-row sm:items-center sm:justify-between lg:p-8">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#FFF2E8] text-[#0E2A47]"><LifeBuoy size={22} /></div>
            <div>
              <h2 className="font-serif text-3xl font-semibold text-[#0E2A47]">Help when you need it.</h2>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-[#0E2A47]/75">Questions about ordering, pickup, delivery, or your account? Arctic Plate support is built directly into the platform.</p>
            </div>
          </div>
          <Link to="/support" className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full border border-[#0E2A47] px-5 text-xs font-extrabold text-[#0E2A47]">
            Contact support <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <section className="border-t border-[#E7E5E4] bg-white px-5 py-10 sm:px-8 lg:px-14 lg:py-14" data-testid="home-faq">
        <div className="mx-auto max-w-[1000px]">
          <div className="text-[10px] font-extrabold uppercase tracking-[0.28em] text-[#C25A2E]">Common questions</div>
          <h2 className="mt-1 font-serif text-4xl font-semibold text-[#0E2A47]">Before you order</h2>
          <div className="mt-5 divide-y divide-[#F1E4D6] border-y border-[#F1E4D6]">
            {FAQS.map((item) => (
              <details key={item.q} className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-extrabold text-[#0E2A47]">
                  {item.q}
                  <span className="text-xl font-light text-[#C25A2E] transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="max-w-3xl pb-2 pt-3 text-sm leading-6 text-[#0E2A47]/75">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
      </>}
    </>
  );
}
